/**
 * Direction Priors Tab
 * Admin-only diagnostic for the empirical (league x prop x direction) scoring
 * prior. Reads the direction_priors table (GET /api/admin/direction-priors); the
 * same data scoring applies (cells at n>=50) via the in-memory cache.
 */

import { LoadingSpinner } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../../config/api';

interface DirectionPriorRow {
  leagueKey: string;
  propType: string;
  betDirection: string;
  sampleCount: number;
  hitRate: number;
  offset: number;
  usable: boolean;
  computedAt: string;
}

/** Break-even hit rate at -110. Above = green, below = red, at = neutral. */
const BREAKEVEN = 0.524;

function hitRateColor(hitRate: number): string {
  if (hitRate >= BREAKEVEN + 0.02) return 'text-theme-success';
  if (hitRate <= BREAKEVEN - 0.02) return 'text-theme-danger';
  return 'text-theme-text';
}

/** Shared select styling, mirrors UserFilters. */
const SELECT_CLASS =
  'px-4 py-2 bg-theme-bg border border-theme-border rounded-lg text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary';

type UsabilityFilter = 'all' | 'usable' | 'below';

export function DirectionPriorsTab(): ReactElement {
  const { authFetch } = useAuth();
  const [data, setData] = useState<DirectionPriorRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leagueFilter, setLeagueFilter] = useState('');
  const [propFilter, setPropFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [usabilityFilter, setUsabilityFilter] = useState<UsabilityFilter>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl('/api/admin/direction-priors'));
      if (!response.ok) {
        throw new Error('Failed to fetch direction priors');
      }
      const result = (await response.json()) as DirectionPriorRow[];
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Distinct filter options, derived from the loaded rows (small dataset, all client-side).
  const leagueOptions = useMemo(
    () => Array.from(new Set((data ?? []).map((r) => r.leagueKey))).sort(),
    [data],
  );
  const propOptions = useMemo(
    () => Array.from(new Set((data ?? []).map((r) => r.propType))).sort(),
    [data],
  );
  const directionOptions = useMemo(
    () => Array.from(new Set((data ?? []).map((r) => r.betDirection))).sort(),
    [data],
  );

  const filteredData = useMemo(() => {
    return (data ?? []).filter((r) => {
      if (leagueFilter && r.leagueKey !== leagueFilter) return false;
      if (propFilter && r.propType !== propFilter) return false;
      if (directionFilter && r.betDirection !== directionFilter) return false;
      if (usabilityFilter === 'usable' && !r.usable) return false;
      if (usabilityFilter === 'below' && r.usable) return false;
      return true;
    });
  }, [data, leagueFilter, propFilter, directionFilter, usabilityFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-theme-danger">{error || 'Failed to load direction priors'}</p>
        <button
          type="button"
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const usableCount = data.filter((r) => r.usable).length;
  const leagues = new Set(data.map((r) => r.leagueKey)).size;
  const lastComputed = data.reduce<string | null>(
    (max, r) => (max == null || r.computedAt > max ? r.computedAt : max),
    null,
  );

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Cells', value: String(data.length) },
          { label: 'Usable (n≥50)', value: String(usableCount) },
          { label: 'Leagues', value: String(leagues) },
          {
            label: 'Computed',
            value: lastComputed ? new Date(lastComputed).toLocaleDateString() : '—',
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border"
          >
            <div className="text-xs text-theme-text-secondary">{s.label}</div>
            <div className="text-xl font-semibold text-theme-text">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="bg-theme-bg rounded-lg p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-1">
          Direction Prior (league &times; prop &times; direction)
        </h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          Empirical settled hit rate and the bounded score offset scoring applies. Only cells at
          n&ge;50 (Usable) feed scoring; the rest fall back to the flat constants.
        </p>

        {/* Filters */}
        <div className="flex flex-col md:flex-row flex-wrap gap-3 mb-4">
          <select
            aria-label="Filter by league"
            value={leagueFilter}
            onChange={(e) => setLeagueFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All Leagues</option>
            {leagueOptions.map((league) => (
              <option key={league} value={league}>
                {league.replace('basketball_', '')}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by prop"
            value={propFilter}
            onChange={(e) => setPropFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All Props</option>
            {propOptions.map((prop) => (
              <option key={prop} value={prop}>
                {formatStatLabel(prop)}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by direction"
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">All Directions</option>
            {directionOptions.map((direction) => (
              <option key={direction} value={direction}>
                {direction}
              </option>
            ))}
          </select>
          <select
            aria-label="Filter by usability"
            value={usabilityFilter}
            onChange={(e) => setUsabilityFilter(e.target.value as UsabilityFilter)}
            className={SELECT_CLASS}
          >
            <option value="all">All Cells</option>
            <option value="usable">Usable only</option>
            <option value="below">Below threshold</option>
          </select>
        </div>

        <p className="text-xs text-theme-text-secondary mb-2">
          Showing {filteredData.length} of {data.length} cells
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-theme-border">
                <th className="text-left py-2 px-4 text-theme-text-secondary">League</th>
                <th className="text-left py-2 px-4 text-theme-text-secondary">Prop</th>
                <th className="text-left py-2 px-4 text-theme-text-secondary">Direction</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Hit Rate</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Sample</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Offset</th>
                <th className="text-right py-2 px-4 text-theme-text-secondary">Usable</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-theme-text-secondary">
                    No cells match the selected filters.
                  </td>
                </tr>
              )}
              {filteredData.map((r) => (
                <tr
                  key={`${r.leagueKey}|${r.propType}|${r.betDirection}`}
                  className={`border-b border-theme-border hover:bg-theme-bg-secondary ${
                    r.usable ? '' : 'opacity-50'
                  }`}
                >
                  <td className="py-2 px-4 text-theme-text">
                    {r.leagueKey.replace('basketball_', '')}
                  </td>
                  <td className="py-2 px-4 text-theme-text whitespace-nowrap">
                    {formatStatLabel(r.propType)}
                  </td>
                  <td className="py-2 px-4 text-theme-text">{r.betDirection}</td>
                  <td className={`text-right py-2 px-4 ${hitRateColor(r.hitRate)}`}>
                    {(r.hitRate * 100).toFixed(1)}%
                  </td>
                  <td className="text-right py-2 px-4 text-theme-text">{r.sampleCount}</td>
                  <td className="text-right py-2 px-4 text-theme-text font-mono">
                    {r.offset > 0 ? `+${r.offset}` : r.offset}
                  </td>
                  <td className="text-right py-2 px-4 text-theme-text">{r.usable ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
